import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { useRouter } from '~/utils/navigation';
import Cookies from '@comeon/tomcat-cookies';
import {
	notificationPopupCTAClicked,
	notificationCarouselClosed,
	notificationCarouselTnCClicked
} from '@comeon/analytics';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import useTranslations from '~/components/translation/use-translations.ts';
import MyNotificationsNavigationDots from './my-notification-navigation-dots.js';
import Icon from '~/components/icon/icon.js';
import { ICON_CLOSE } from '~/components/icon/index.js';

import handleCTAClick from '../my-notifications-utils.js';
import { updateMyNotificationsPopupAdditionalData } from '../ducks/my-notifications.js';
import RichTextRenderer from '../rich-text-renderer';

import useBasePathname from '~/hooks/use-base-pathname';

const ImageWrapper = styled.img`
	object-fit: contain;
	width: 100%;
	margin-bottom: 15px;
	height: 200px;
`;

const ButtonWrapper = styled.div`
	padding: ${props => (props.small ? '0 24px' : '0 70px')};
	display: flex;
	justify-content: center;
	position: absolute;
	bottom: 60px;
	left: 0;
	width: 100%;
`;

const Button = styled.button`
	color: rgba(var(--notification-popup-button-bg), 1);
	border: 1px solid rgba(var(--notification-popup-button-bg), 1);
	display: inline-block;
	font: ${props => (props.small ? 'var(--h3)' : 'var(--heading-5)')};
	text-align: center;
	white-space: break-spaces;
	word-break: break-word;
	padding: ${props => (props.small ? '8px 25px' : '8px 40px')};
	border-radius: 26px;
`;

const CloseWrapper = styled.button`
	margin-bottom: 8px;
	color: rgba(var(--notification-popup-close-icon), 1);
`;

const ReadMoreWrapper = styled.div`
	position: relative;
	max-width: 100%;
	height: 100%;
	max-height: ${props => (props.maxAvailableHeight ? `${props.maxAvailableHeight}px` : 'none')};
	overflow: hidden;
	cursor: ${props => (props.isTruncated ? 'pointer' : 'default')};
`;

const StyledReadMoreBUtton = styled.button`
	color: rgb(var(--embed-link-color));
	bottom: 0;
	right: 0;
	background-color: rgba(var(--notification-popup-bg), 1);
	padding: ${props => (props.small ? '3px 15px' : '3px 24px')};
	position: absolute;
`;

const ShowAdditionalDataButton = styled.div`
	font-size: 16px;
	padding: 15px 8px;
	text-align: center;
	position: absolute;
	width: ${props => (props.small ? 'calc(100% - 20px)' : 'calc(100% - 12px)')};
	bottom: ${props => (props.small ? '99px' : '117px')};
`;

const secondsSinceLogin = () => {
	const loggedInTime = Cookies.get('loggedInTime');
	const time = (Date.now() - loggedInTime) / 1000;

	return time.toFixed(1);
};

const isEllipsisActive = element => {
	return element?.clientHeight < element?.scrollHeight;
};

const MyNotificationSliderFront = ({
	id,
	recurringNotificationId,
	subtitle,
	image,
	title,
	ctaText,
	ctaUrl,
	ctaUrlType,
	ctaUrlPresetType,
	numberSeenItems,
	navigationDotsCount,
	showCloseButton,
	handleCloseButtonClick,
	activeNavigationDotsIndex,
	isMobileDevice,
	popupAdditionalData,
	markSeenItemsAsReceived,
	maxAvailableHeight
}) => {
	const ref = useRef(null);
	const dispatch = useDispatch();
	const router = useRouter();
	const basePathname = useBasePathname();

	const translationTexts = [`POP_UP_DESCRIPTION_READ_MORE`];

	const { translations } = useTranslations(translationTexts);

	const handleCTACallback = () => {
		notificationPopupCTAClicked(
			`${recurringNotificationId ? `r${recurringNotificationId} - ` : ''}${id} - ${title}`
		);
		notificationCarouselClosed(`${numberSeenItems}/${navigationDotsCount}`);
		handleCTAClick(
			{
				ctaUrl,
				ctaUrlType,
				ctaUrlPresetType
			},
			router,
			basePathname
		);
		markSeenItemsAsReceived();
	};

	const handleCloseAdditionalPopupClick = () => {
		dispatch(updateMyNotificationsPopupAdditionalData(null));
	};

	// Click on truncated `read more`
	const handleTruncatedClick = () => {
		if (ref?.current && isEllipsisActive(ref.current)) {
			// Show the popup with full subtitle here
			dispatch(
				updateMyNotificationsPopupAdditionalData({
					title,
					subtitle,
					ctaText,
					ctaCallback: handleCTACallback,
					onCloseClick: handleCloseAdditionalPopupClick,
					isMobileDevice
				})
			);
		}
	};

	const handleShowPopupAdditionalData = () => {
		// This is clicking the additional data button
		// SHow the additional data in the popup here
		notificationCarouselTnCClicked(`${id} - ${title}`);

		dispatch(
			updateMyNotificationsPopupAdditionalData({
				title: popupAdditionalData.PopupAdditionalData.title,
				subtitle: popupAdditionalData.PopupAdditionalData.content,
				ctaText,
				ctaCallback: handleCTACallback,
				onCloseClick: handleCloseAdditionalPopupClick,
				isMobileDevice
			})
		);
	};

	return (
		<div className="my-notification-slider-item-front">
			{showCloseButton && (
				<CloseWrapper
					onClick={() => {
						handleCloseButtonClick(
							`${
								recurringNotificationId ? `r${recurringNotificationId} - ` : ''
							}${id} - ${title} - ${secondsSinceLogin()}`
						);
					}}
				>
					<Icon type={ICON_CLOSE} />
				</CloseWrapper>
			)}

			{image && <ImageWrapper src={image} />}

			<ReadMoreWrapper
				maxAvailableHeight={maxAvailableHeight}
				onClick={handleTruncatedClick}
				ref={ref}
				isTruncated={ref?.current && isEllipsisActive(ref?.current)}
			>
				{/* Title */}
				{title && (
					<RichTextRenderer
						data={title}
						key="title"
						additionalClassName="my-notification-popup-title"
						additionalClassNameEditor="my-notification-popup-title_editor"
					/>
				)}

				{/* Subtitle */}
				{subtitle && (
					<RichTextRenderer
						data={subtitle}
						key="subtitle"
						additionalClassName="my-notification-popup-subtitle"
						additionalClassNameEditor="my-notification-popup-subtitle_editor"
					/>
				)}

				{ref?.current && isEllipsisActive(ref.current) && (
					<StyledReadMoreBUtton small={isMobileDevice} onClick={handleTruncatedClick}>
						{translations.POP_UP_DESCRIPTION_READ_MORE}
					</StyledReadMoreBUtton>
				)}
			</ReadMoreWrapper>

			{/* Popup Additional Data: Title */}
			{popupAdditionalData?.PopupAdditionalData?.title && (
				<ShowAdditionalDataButton>
					<button onClick={handleShowPopupAdditionalData}>
						<RichTextRenderer
							data={popupAdditionalData.PopupAdditionalData.title}
							key="popup-additional-data-title"
							additionalClassName="my-notification-slider-additional-data-button"
							additionalClassNameEditor="my-notification-slider-additional-data-button_editor"
						/>
					</button>
				</ShowAdditionalDataButton>
			)}

			{ctaText && (
				<ButtonWrapper small={isMobileDevice}>
					<Button small={isMobileDevice} onClick={handleCTACallback}>
						{ctaText}
					</Button>
				</ButtonWrapper>
			)}
			<div className="my-notification-slider-navigation-dots-wrapper">
				<MyNotificationsNavigationDots length={navigationDotsCount} activeIndex={activeNavigationDotsIndex} />
			</div>
		</div>
	);
};

const MyNotificationSliderItem = ({
	recurringNotificationId,
	notificationPopUpData,
	navigationDotsCount,
	activeNavigationDotsIndex,
	handleCloseButtonClick,
	showCloseButton,
	numberSeenItems,
	isMobileDevice,
	markSeenItemsAsReceived,
	maxAvailableHeight
}) => {
	const ref = useRef(null);

	useEffect(() => {
		disableBodyScroll(ref.current);

		return () => clearAllBodyScrollLocks();
	}, []);

	const {
		id,
		image,
		title,
		subtitle,
		ctaText,
		ctaUrl,
		ctaUrlType,
		ctaUrlPresetType,
		data,
		bannerKey,
		personalizationData
	} = notificationPopUpData || {};

	return (
		<div className="my-notification-slider-item-container" ref={ref}>
			<MyNotificationSliderFront
				id={id}
				recurringNotificationId={recurringNotificationId}
				subtitle={subtitle}
				image={image}
				bannerKey={bannerKey}
				personalizationData={personalizationData}
				title={title}
				ctaText={ctaText}
				ctaUrl={ctaUrl}
				ctaUrlType={ctaUrlType}
				ctaUrlPresetType={ctaUrlPresetType}
				numberSeenItems={numberSeenItems}
				navigationDotsCount={navigationDotsCount}
				showCloseButton={showCloseButton}
				handleCloseButtonClick={handleCloseButtonClick}
				activeNavigationDotsIndex={activeNavigationDotsIndex}
				isMobileDevice={isMobileDevice}
				popupAdditionalData={data}
				markSeenItemsAsReceived={markSeenItemsAsReceived}
				maxAvailableHeight={maxAvailableHeight}
			/>
		</div>
	);
};

export default MyNotificationSliderItem;
